// src/layouts/MainLayout/MainLayout.jsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { useUser } from "hooks/useUser";
import { logout } from "services/auth";
import { Button } from "components/ui/button";
import { ThemeToggle } from "components/ThemeToggle";
import logoEmpresa from "assets/images/Arvore timbo transparente colorida.png";
import { Menu } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetClose,
  SheetTrigger,
} from "components/ui/sheet";

function MainLayout({ children }) {
  const { user } = useAuth();
  const { isAdmin, canViewDashboard } = useUser();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      alert("Ocorreu um erro ao sair. Tente novamente.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50 dark:bg-slate-900">
      <header className="sticky top-0 z-40 w-full border-b bg-background">
        <div className="container flex h-16 items-center justify-between">
          <Link
            to="/"
            className="flex items-center space-x-3 rtl:space-x-reverse"
          >
            <img
              src={logoEmpresa}
              alt="Logo da Empresa"
              className="h-8 w-auto"
            />
            <span className="self-center text-xl font-semibold whitespace-nowrap">
              Gestão Timbó
            </span>
          </Link>

          <nav className="hidden md:flex items-center space-x-2">
            {canViewDashboard && (
              <Button asChild variant="ghost">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
            )}
            {isAdmin && (
              <Button asChild variant="ghost">
                <Link to="/clientes">Clientes</Link>
              </Button>
            )}
            {isAdmin && (
              <Button asChild variant="ghost">
                <Link to="/creditos">Créditos</Link>
              </Button>
            )}
            {isAdmin && (
              <Button asChild variant="ghost">
                <Link to="/admin">Admin</Link>
              </Button>
            )}
            <div className="text-sm text-right">
              <span className="text-muted-foreground">Olá,</span>
              <span className="font-semibold ml-1">
                {user?.displayName || user?.email}
              </span>
            </div>
            <ThemeToggle />
            <Button onClick={handleLogout} variant="outline" size="sm">
              Sair
            </Button>
          </nav>

          <div className="md:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left">
                <div className="flex flex-col h-full">
                  <nav className="flex flex-col space-y-4 text-lg mt-8">
                    <SheetClose asChild>
                      <Link to="/">Página Inicial</Link>
                    </SheetClose>
                    {canViewDashboard && (
                      <SheetClose asChild>
                        <Link to="/dashboard">Dashboard</Link>
                      </SheetClose>
                    )}
                    {isAdmin && (
                      <SheetClose asChild>
                        <Link to="/clientes">Clientes</Link>
                      </SheetClose>
                    )}
                    {isAdmin && (
                      <SheetClose asChild>
                        <Link to="/creditos">Créditos</Link>
                      </SheetClose>
                    )}
                    {isAdmin && (
                      <SheetClose asChild>
                        <Link to="/admin">Admin</Link>
                      </SheetClose>
                    )}
                  </nav>

                  <div className="mt-auto space-y-4">
                    <div className="text-sm text-left border-t pt-4">
                      <span className="font-semibold">
                        {user?.displayName || user?.email}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <ThemeToggle />
                      <SheetClose asChild>
                        <Button
                          onClick={handleLogout}
                          variant="outline"
                          size="sm"
                        >
                          Sair
                        </Button>
                      </SheetClose>
                    </div>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-grow p-4 md:p-6">{children}</main>
    </div>
  );
}

export default MainLayout;
